import { useSyncExternalStore } from 'react'

/** Light/dark choice, remembered per device. 'system' follows the device setting. */
export type ThemeChoice = 'system' | 'light' | 'dark'
export const THEME_KEY = 'lqp:ui:theme'

const listeners = new Set<() => void>()

function systemDark(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  } catch {
    return true
  }
}

export function readChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(THEME_KEY)
    return v === 'light' || v === 'dark' ? v : 'system'
  } catch {
    return 'system'
  }
}

export function resolveTheme(choice: ThemeChoice, prefersDark: boolean): 'light' | 'dark' {
  return choice === 'system' ? (prefersDark ? 'dark' : 'light') : choice
}

/** Puts the theme on <html> (the same work index.html does before first paint). */
export function applyTheme(choice: ThemeChoice = readChoice()) {
  const t = resolveTheme(choice, systemDark())
  document.documentElement.dataset.theme = t
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', t === 'light' ? '#ffffff' : '#14171c')
}

export function setThemeChoice(choice: ThemeChoice) {
  try {
    if (choice === 'system') localStorage.removeItem(THEME_KEY)
    else localStorage.setItem(THEME_KEY, choice)
  } catch {
    /* storage blocked: still switch for this visit */
  }
  current = choice
  applyTheme(choice)
  listeners.forEach((l) => l())
}

let current: ThemeChoice | null = null
let watching = false

function subscribe(l: () => void) {
  listeners.add(l)
  if (!watching) {
    watching = true
    try {
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => applyTheme(current ?? readChoice()))
    } catch {
      /* old browser: no live system changes */
    }
  }
  return () => {
    listeners.delete(l)
  }
}

export function useThemeChoice(): [ThemeChoice, (c: ThemeChoice) => void] {
  const choice = useSyncExternalStore(subscribe, () => current ?? (current = readChoice()), () => 'system' as ThemeChoice)
  return [choice, setThemeChoice]
}

export const THEME_OPTIONS: { value: ThemeChoice; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
]
